/**
 * Currency conversion and formatting utilities using integer cents.
 */

/**
 * Converts integer cents into a major currency unit amount.
 *
 * @param {number} cents Integer value (e.g. 15000)
 * @returns {number} Decimal representation (e.g. 150.00)
 */
export function centsToAmount(cents) {
  if (typeof cents !== 'number' || isNaN(cents)) {
    return 0;
  }
  return Math.round(cents) / 100;
}

/**
 * Converts a decimal monetary amount into integer cents.
 *
 * @param {number|string} amount Decimal value (e.g. 150 or "150.50")
 * @returns {number} Integer cents (e.g. 15050)
 */
export function amountToCents(amount) {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (typeof num !== 'number' || isNaN(num)) {
    return 0;
  }
  return Math.round(num * 100);
}

/**
 * Formats a centimes value into a localized currency string.
 *
 * @param {number} cents Monetary amount in integer cents
 * @param {string} [currency='MAD'] ISO Currency code
 * @param {string} [language='fr'] Language code ('fr' | 'ar')
 * @returns {string} Formatted string (e.g. "150,00 MAD" or "150.00 د.م.")
 */
export function formatMoney(cents, currency = 'MAD', language = 'fr') {
  const amount = centsToAmount(cents);
  const isArabic = language === 'ar';
  
  const formattedNumber = new Intl.NumberFormat(isArabic ? 'ar-MA' : 'fr-MA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  if (isArabic) {
    const symbol = currency === 'MAD' ? 'د.م.' : currency;
    return `${formattedNumber} ${symbol}`;
  }

  return `${formattedNumber} ${currency}`;
}

/**
 * Calculates percentage discount between regular price and sale price.
 *
 * @param {number} regularCents
 * @param {number} saleCents
 * @returns {number} Rounded percentage discount (e.g. 25 for 25% off)
 */
export function calculateDiscountPercent(regularCents, saleCents) {
  if (!regularCents || !saleCents || saleCents >= regularCents) {
    return 0;
  }
  return Math.round(((regularCents - saleCents) / regularCents) * 100);
}
